import React, { useEffect, useMemo, useRef, useState } from 'react';

const suggestions = [
  ['Payday', 'payday'],
  ['Clock in', 'clock in'],
  ['W-2', 'W-2'],
  ['Background checks', 'background check'],
];

function WorkerFaq({ base }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [copied, setCopied] = useState('');
  const input = useRef(null);
  const results = useRef(null);

  useEffect(() => {
    let active = true;
    fetch(`${base}/worker-faq-data.json`)
      .then(response => {
        if (!response.ok) throw new Error('FAQ unavailable');
        return response.json();
      })
      .then(value => {
        if (active) setData(value);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [base]);

  useEffect(() => {
    const onKeyDown = event => {
      const target = event.target;
      const typing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (event.key === '/' && !typing) {
        event.preventDefault();
        input.current && input.current.focus();
      } else if (
        event.key === 'Escape' &&
        document.activeElement === input.current
      ) {
        setQuery('');
        setFilter('all');
        input.current.blur();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const normalized = query
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const visible = useMemo(
    () =>
      data
        ? data.items.filter(
            item =>
              (filter === 'all' || item.group === filter) &&
              (!normalized || item.searchText.includes(normalized)),
          )
        : [],
    [data, filter, normalized],
  );

  useEffect(() => {
    if (!data) return undefined;
    const openHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      const item = id && document.getElementById(id);
      if (item && item.tagName === 'DETAILS') {
        item.open = true;
        item.scrollIntoView({ block: 'start' });
      }
    };
    const timer = window.setTimeout(openHash, 0);
    window.addEventListener('hashchange', openHash);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('hashchange', openHash);
    };
  }, [data, filter, normalized]);

  const counts = data
    ? Object.fromEntries(
        data.groups.map(group => [
          group.id,
          data.items.filter(item => item.group === group.id).length,
        ]),
      )
    : {};
  const status = normalized
    ? `Found ${visible.length} ${
        visible.length === 1 ? 'topic' : 'topics'
      } for “${query.trim()}”`
    : filter === 'all'
    ? `Showing all ${visible.length} topics`
    : `Showing ${visible.length} ${
        visible.length === 1 ? 'topic' : 'topics'
      } in this category`;

  const reset = () => {
    setQuery('');
    setFilter('all');
    input.current && input.current.focus();
  };
  const chooseSuggestion = value => {
    setQuery(value);
    setFilter('all');
    input.current && input.current.focus();
  };
  const toggleVisible = open => {
    if (!results.current) return;
    results.current.querySelectorAll('details').forEach(item => {
      item.open = open;
    });
  };
  const copyLink = id => {
    const link = `${window.location.origin}${window.location.pathname}#${id}`;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(link)
        .then(() => {
          setCopied(id);
          window.setTimeout(() => setCopied(''), 1800);
        })
        .catch(() => {
          window.location.hash = id;
        });
    } else {
      window.location.hash = id;
    }
  };

  return (
    <div className="worker-faq">
      <header className="worker-faq__hero">
        <div className="m-container">
          <p className="m-eyebrow">Spur worker help center</p>
          <h1>What can we help you figure out?</h1>
          <p>
            Search {data ? data.items.length : 'our'} answers covering jobs,
            onboarding, pay, benefits, qualifications, safety, and more.
          </p>
          <div className="worker-faq__search">
            <label htmlFor="worker-faq-search">Search worker support</label>
            <div>
              <span aria-hidden="true">⌕</span>
              <input
                ref={input}
                id="worker-faq-search"
                type="search"
                autoComplete="off"
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Search worker support…"
              />
              {query && (
                <button type="button" onClick={reset}>
                  Clear
                </button>
              )}
              <span className="worker-faq__shortcut" aria-hidden="true">
                /
              </span>
            </div>
          </div>
          <div className="worker-faq__popular" aria-label="Popular searches">
            <span>Popular:</span>
            {suggestions.map(([label, value]) => (
              <button
                type="button"
                key={label}
                onClick={() => chooseSuggestion(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <section
        className="worker-faq__emergency m-container"
        aria-label="Urgent injury help"
      >
        <div>
          <strong>Injured at work?</strong> For a life-threatening emergency,
          call 911. Otherwise, notify Spur immediately.
        </div>
        <a href="tel:12563678402">Call Spur: (256) 367-8402</a>
      </section>

      {error ? (
        <div className="worker-faq__message m-container" role="alert">
          We couldn't load the worker FAQ. Please try again or contact{' '}
          <a href="mailto:support@spurstaffing.com">Spur Support</a>.
        </div>
      ) : !data ? (
        <div className="worker-faq__message m-container" role="status">
          Loading answers…
        </div>
      ) : (
        <div className="worker-faq__layout m-container">
          <aside className="worker-faq__sidebar" aria-label="FAQ categories">
            <h2>Browse by topic</h2>
            <button
              type="button"
              className={filter === 'all' ? 'active' : ''}
              aria-pressed={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              <span>All topics</span>
              <span>{data.items.length}</span>
            </button>
            {data.groups
              .filter(group => counts[group.id])
              .map(group => (
                <button
                  type="button"
                  key={group.id}
                  className={filter === group.id ? 'active' : ''}
                  aria-pressed={filter === group.id}
                  onClick={() => setFilter(group.id)}
                >
                  <span>{group.label}</span>
                  <span>{counts[group.id]}</span>
                </button>
              ))}
            <p>
              <strong>Process details can change.</strong> If the app or your
              workplace gives different instructions, follow those instructions
              or contact Spur Support.
            </p>
          </aside>
          <div className="worker-faq__results" ref={results}>
            <div className="worker-faq__toolbar">
              <p role="status" aria-live="polite">
                {status}
              </p>
              <div>
                <button type="button" onClick={() => toggleVisible(true)}>
                  Expand visible
                </button>
                <button type="button" onClick={() => toggleVisible(false)}>
                  Collapse all
                </button>
              </div>
            </div>
            {data.groups.map(group => {
              const items = visible.filter(item => item.group === group.id);
              if (!items.length) return null;
              return (
                <section className="worker-faq__section" key={group.id}>
                  <p className="worker-faq__count">
                    {items.length} {items.length === 1 ? 'topic' : 'topics'}
                  </p>
                  <h2>{group.label}</h2>
                  <p>{group.description}</p>
                  <div className="worker-faq__list">
                    {items.map(item => (
                      <details
                        key={item.id}
                        id={item.id}
                        className="worker-faq__item"
                      >
                        <summary>
                          <span>{item.title}</span>
                          <small className={item.review ? 'review' : ''}>
                            {item.review
                              ? 'Confirm current process'
                              : item.sourceType === 'core'
                              ? 'Quick answer'
                              : 'Detailed guide'}
                          </small>
                        </summary>
                        <div className="worker-faq__answer">
                          {item.notice && (
                            <div className="worker-faq__notice">
                              <strong>Before you rely on this</strong>
                              <p>{item.notice}</p>
                            </div>
                          )}
                          <div
                            dangerouslySetInnerHTML={{
                              __html: item.html.replace(
                                /href="\/([^"]+)"/g,
                                `href="${base}/$1"`,
                              ),
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => copyLink(item.id)}
                          >
                            {copied === item.id
                              ? 'Link copied'
                              : 'Copy link to this answer'}
                          </button>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              );
            })}
            {!visible.length && (
              <div className="worker-faq__empty">
                <h2>No answers found</h2>
                <p>Try a different term or browse all topics.</p>
                <button type="button" onClick={reset}>
                  Show all topics
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkerFaq;
